/* Importing the necessary libraries and files */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { Form, FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

/* Importing the components from the material-ui library. */
import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers';

/* Importing the functions from the Redux slices. */
import { getPatient, postPatient, putPatient } from '../../features/patient/patientSlice';

/* Importing the DateTimePatientCards component from the components folder. */
import DateTimePatientCards from '../../components/DateTimePatientCards';
import Page from '../../components/Page';

/* Importing from the utils folder. */
import fetchAndHandleData from '../../utils/fetchAndHandleData';
import getInitialValues from '../../utils/getInitialValues';
import handleFormResponse from '../../utils/handleFormResponse';
import { fPatient } from '../../utils/Patient/Patient.utils.format';
import { mPatient } from '../../utils/Patient/Patient.utils.map';
import { administrativeGender, maritalStatus } from '../../utils/Patient/Patient.utils.ValueSets';

/* Defining the propTypes for the PatientRegister component. */
PatientRegister.propTypes = {
  isEdit: PropTypes.bool,
};

/**
 * This component is responsible for registering a patient.
 * It allows the user to enter patient information and submit the form.
 * If the form submission is successful, the user is navigated to the patient details page.
 * If the form submission fails, an error message is displayed.
 * @param {Object} props - The component props.
 * @param {boolean} props.isEdit - Indicates whether the component is in edit mode or not.
 * @returns {JSX.Element} The PatientRegister component.
 */
export default function PatientRegister(props) {
  const { isEdit } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const { Patients } = useSelector((state) => state.patient);
  const currentPatient = Patients.find((el) => el.id === params.id);

  /* Setting the initial state of the pagePatient object. */
  const [pagePatient, setPagePatient] = useState({});
  const [title, setTitle] = useState('Register Patient');

  const defaultValues = useMemo(
    () => ({
      name_given: '',
      name_family: '',
      name_suffix: '',
      gender: '',
      birthDate: '',
      address_line: '',
      address_city: '',
      address_postalCode: '',
      address_country: 'Philippines',
      photo_url: '',
      deceasedBoolean: false,
      maritalStatus_code: '',
      telecom_phone_value: '',
      telecom_email_value: '',
      contact_name_text: '',
      contact_relationship_text: '',
      contact_telecom_value: '',
      'philhealthNo-id': '',
      'SSSNo-id': '',
      'GSISNo-id': '',
      'patient-pregnancyStatus': false,
      'patient-birthPlace': '',
      'patient-hmo': '',
      'patient-mothersMaidenName': '',
      'patient-nationality': '',
      'patient-remarks': '',
      'patient-lifeline-config-ecg_st_msec': 0,
      'patient-lifeline-config-heartrate_upper_bpm': 100,
      'patient-lifeline-config-heartrate_lower_bpm': 50,
      'patient-lifeline-config-pulserate_upper_bpm': 100,
      'patient-lifeline-config-pulserate_lower_bpm': 50,
      'patient-lifeline-config-oxygen_upper_saturation': 100,
      'patient-lifeline-config-oxygen_lower_saturation': 94,
      'patient-lifeline-config-respiratory_upper_rpm': 20,
      'patient-lifeline-config-respiratory_lower_rpm': 12,
      'patient-lifeline-config-bp_systolic_upper': 120,
      'patient-lifeline-config-bp_systolic_lower': 90,
      'patient-lifeline-config-bp_diastolic_upper': 80,
      'patient-lifeline-config-bp_diastolic_lower': 60,
      'patient-lifeline-config-time_frame': 30,
      'patient-lifeline-config-temperature_upper': 42,
      'patient-lifeline-config-temperature_lower': 35.8,
    }),
    []
  );

  /* Creating a schema for the form. */
  const PatientSchema = Yup.object().shape({
    name_given: Yup.string().min(1, 'Too Short!').max(50, 'Too Long!').required('First name required'),
    name_family: Yup.string().min(1, 'Too Short!').max(50, 'Too Long!').required('Last name required'),
  });

  /* A formik hook that is used to validate the form. */
  const formik = useFormik({
    initialValues: getInitialValues({
      defaultValues,
      data: pagePatient,
    }),
    validationSchema: PatientSchema,
    onSubmit: (values) => {
      const patientData = mPatient(values);
      if (isEdit) {
        submitPutPatient(patientData);
      } else {
        submitPostPatient(patientData);
      }
    },
  });

  const submitPostPatient = async (values) => {
    const resultAction = await dispatch(postPatient(values));
    await handleFormResponse({
      resultAction,
      successTitle: 'Patient added successfully!',
      navigatePath: '/patient/details/:id',
      navigate,
    });
  };

  const submitPutPatient = async (values) => {
    const data = {
      patientID: params.id,
      data: { ...values, id: params.id },
    };
    const resultAction = await dispatch(putPatient(data));
    await handleFormResponse({
      resultAction,
      successTitle: 'Patient updated successfully!',
      navigatePath: '/patient/details/:id',
      navigate,
    });
  };

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, setFieldValue, setValues, values } = formik;

  useEffect(() => {
    setTitle(
      isEdit && (pagePatient.name_family || pagePatient.name_given)
        ? `Edit Patient - ${pagePatient.name_family}, ${pagePatient.name_given} ${pagePatient.name_suffix}`
        : 'Register Patient'
    );
  }, [isEdit, pagePatient.name_family, pagePatient.name_given, pagePatient.name_suffix]);

  useEffect(() => {
    if (isEdit && currentPatient) {
      const patientObj = fPatient(currentPatient);
      setPagePatient(patientObj);
      setValues(getInitialValues({ data: patientObj, defaultValues }));
    }
  }, [isEdit, currentPatient, setValues, defaultValues]);

  /* A function that is called when the page is loaded. */
  const initializeData = useCallback(async () => {
    if (isEdit) {
      fetchAndHandleData({
        dispatch,
        navigate,
        fetchAction: getPatient,
        id: params.id,
        errorMessage: 'No Patient record found',
        navigateOnFailure: true,
        navigateTo: '/patient/list/',
      });
    }
  }, [dispatch, isEdit, navigate, params.id]);

  /**
   * Initializes the data for the page.
   */
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  return (
    <Page title={title}>
      <Container maxWidth="none">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Stack direction="column" justifyContent="space-between">
            <Typography variant="h4" gutterBottom>
              {title}
            </Typography>
            <Breadcrumbs aria-label="breadcrumb" mr={4}>
              <Link underline="hover" color="inherit" component={RouterLink} to="/patient/list">
                Patient
              </Link>

              {isEdit && (
                <Link underline="hover" color="inherit" component={RouterLink} to={`/patient/details/${params.id}`}>
                  {pagePatient?.name_family || ''}, {pagePatient?.name_given || ''} {pagePatient?.name_suffix || ''}
                </Link>
              )}

              {isEdit ? (
                <Typography color="text.primary">Edit</Typography>
              ) : (
                <Typography color="text.primary">Register</Typography>
              )}
            </Breadcrumbs>
          </Stack>
        </Stack>

        <DateTimePatientCards />

        <Grid container>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <FormikProvider value={formik}>
                  <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    <Grid item xs={12}>
                      <Typography variant="h5" sx={{ pb: 1 }}>
                        Personal Information
                      </Typography>
                    </Grid>

                    <Grid container item xs={12} columnSpacing={2} rowSpacing={2}>
                      <Grid item xs={12} md={12}>
                        <Stack spacing={3}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="First Name*"
                              {...getFieldProps('name_given')}
                              error={Boolean(touched.name_given && errors.name_given)}
                              helperText={touched.name_given && errors.name_given}
                            />

                            <TextField
                              fullWidth
                              label="Middle Name"
                              {...getFieldProps('patient-mothersMaidenName')}
                              error={Boolean(
                                touched['patient-mothersMaidenName'] && errors['patient-mothersMaidenName']
                              )}
                              helperText={touched['patient-mothersMaidenName'] && errors['patient-mothersMaidenName']}
                            />

                            <TextField
                              fullWidth
                              label="Last Name*"
                              {...getFieldProps('name_family')}
                              error={Boolean(touched.name_family && errors.name_family)}
                              helperText={touched.name_family && errors.name_family}
                            />
                            <TextField
                              fullWidth
                              label="Suffix"
                              {...getFieldProps('name_suffix')}
                              error={Boolean(touched.name_suffix && errors.name_suffix)}
                              helperText={touched.name_suffix && errors.name_suffix}
                            />
                          </Stack>

                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <DatePicker
                              label="Date of Birth"
                              value={values && values.birthDate ? moment(values.birthDate) : null}
                              onChange={(value) => {
                                setFieldValue(
                                  'birthDate',
                                  value && moment.isMoment(value) && value.isValid()
                                    ? moment(value).format('YYYY-MM-DD')
                                    : ''
                                );
                              }}
                              slotProps={{ textField: { fullWidth: true } }}
                            />

                            <TextField
                              fullWidth
                              label="Birth Place"
                              {...getFieldProps('patient-birthPlace')}
                              error={Boolean(touched['patient-birthPlace'] && errors['patient-birthPlace'])}
                              helperText={touched['patient-birthPlace'] && errors['patient-birthPlace']}
                            />
                          </Stack>

                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Nationality"
                              {...getFieldProps('patient-nationality')}
                              error={Boolean(touched['patient-nationality'] && errors['patient-nationality'])}
                              helperText={touched['patient-nationality'] && errors['patient-nationality']}
                            />
                            <TextField
                              fullWidth
                              select
                              label="Marital Status"
                              {...getFieldProps('maritalStatus_code')}
                              error={Boolean(touched.maritalStatus_code && errors.maritalStatus_code)}
                              helperText={touched.maritalStatus_code && errors.maritalStatus_code}
                            >
                              {maritalStatus.map((el) => (
                                <MenuItem key={el.code} value={el.code}>
                                  {el.display}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          select
                          label="Gender"
                          {...getFieldProps('gender')}
                          onChange={(e) => {
                            formik.setFieldValue('gender', e.target.value);
                            if (e.target.value !== 'female') {
                              formik.setFieldValue('patient-pregnancyStatus', false);
                            }
                          }}
                          error={Boolean(touched.gender && errors.gender)}
                          helperText={touched.gender && errors.gender}
                        >
                          {administrativeGender.map((el) => (
                            <MenuItem key={el.code} value={el.code}>
                              {el.display}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid container item xs={12} sm={6} alignItems="center">
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="patient-pregnancyStatus"
                              checked={values?.['patient-pregnancyStatus'] ?? false}
                              onChange={(e) => {
                                formik.setFieldValue('patient-pregnancyStatus', e.target.checked);
                              }}
                            />
                          }
                          label="Pregnant"
                          disabled={values?.gender !== 'female'}
                        />
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="h5" sx={{ pt: 5, pb: 1 }}>
                        Contact Information
                      </Typography>
                    </Grid>

                    <Grid container item xs={12} spacing={2}>
                      <Grid item xs={12}>
                        <Stack spacing={3}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Contact No."
                              {...getFieldProps('telecom_phone_value')}
                              error={Boolean(touched.telecom_phone_value && errors.telecom_phone_value)}
                              helperText={touched.telecom_phone_value && errors.telecom_phone_value}
                            />

                            <TextField
                              fullWidth
                              label="Email Address"
                              {...getFieldProps('telecom_email_value')}
                              error={Boolean(touched.telecom_email_value && errors.telecom_email_value)}
                              helperText={touched.telecom_email_value && errors.telecom_email_value}
                            />
                          </Stack>
                        </Stack>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ pt: 1 }}>
                          Address
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Stack spacing={3}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Building/House No., Street, Barangay"
                              {...getFieldProps('address_line')}
                              error={Boolean(touched.address_line && errors.address_line)}
                              helperText={touched.address_line && errors.address_line}
                            />
                          </Stack>

                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="City"
                              {...getFieldProps('address_city')}
                              error={Boolean(touched.address_city && errors.address_city)}
                              helperText={touched.address_city && errors.address_city}
                            />

                            <TextField
                              fullWidth
                              label="Country"
                              {...getFieldProps('address_country')}
                              error={Boolean(touched.address_country && errors.address_country)}
                              helperText={touched.address_country && errors.address_country}
                            />

                            <TextField
                              fullWidth
                              label="ZIP Code"
                              {...getFieldProps('address_postalCode')}
                              error={Boolean(touched.address_postalCode && errors.address_postalCode)}
                              helperText={touched.address_postalCode && errors.address_postalCode}
                            />
                          </Stack>
                        </Stack>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ pt: 1 }}>
                          Emergency Contact
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Stack spacing={3}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Emergency Contact's Name"
                              {...getFieldProps('contact_name_text')}
                              error={Boolean(touched.contact_name_text && errors.contact_name_text)}
                              helperText={touched.contact_name_text && errors.contact_name_text}
                            />

                            <TextField
                              fullWidth
                              label="Emergency Contact's No."
                              {...getFieldProps('contact_telecom_value')}
                              error={Boolean(touched.contact_telecom_value && errors.contact_telecom_value)}
                              helperText={touched.contact_telecom_value && errors.contact_telecom_value}
                            />
                          </Stack>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Stack spacing={3}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Relationship to the Patient"
                              {...getFieldProps('contact_relationship_text')}
                              error={Boolean(touched.contact_relationship_text && errors.contact_relationship_text)}
                              helperText={touched.contact_relationship_text && errors.contact_relationship_text}
                            />
                          </Stack>
                        </Stack>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="h5" sx={{ pt: 5, pb: 1 }}>
                        Other Information
                      </Typography>
                    </Grid>

                    <Grid container item xs={12} spacing={2}>
                      <Grid item xs={12}>
                        <Stack spacing={3}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="GSIS No."
                              {...getFieldProps('GSISNo-id')}
                              error={Boolean(touched['GSISNo-id'] && errors['GSISNo-id'])}
                              helperText={touched['GSISNo-id'] && errors['GSISNo-id']}
                            />

                            <TextField
                              fullWidth
                              label="SSS No."
                              {...getFieldProps('SSSNo-id')}
                              error={Boolean(touched['SSSNo-id'] && errors['SSSNo-id'])}
                              helperText={touched['SSSNo-id'] && errors['SSSNo-id']}
                            />
                          </Stack>
                        </Stack>
                      </Grid>

                      <Grid item xs={12}>
                        <Stack spacing={3}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="PhilHealth No."
                              {...getFieldProps('philhealthNo-id')}
                              error={Boolean(touched['philhealthNo-id'] && errors['philhealthNo-id'])}
                              helperText={touched['philhealthNo-id'] && errors['philhealthNo-id']}
                            />

                            <TextField
                              fullWidth
                              label="HMO"
                              {...getFieldProps('patient-hmo')}
                              error={Boolean(touched['patient-hmo'] && errors['patient-hmo'])}
                              helperText={touched['patient-hmo'] && errors['patient-hmo']}
                            />
                          </Stack>

                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Remarks"
                              rows={5}
                              multiline
                              {...getFieldProps('patient-remarks')}
                              error={Boolean(touched['patient-remarks'] && errors['patient-remarks'])}
                              helperText={touched['patient-remarks'] && errors['patient-remarks']}
                            />
                          </Stack>
                        </Stack>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="h5" sx={{ pt: 5, pb: 1 }}>
                        Lifeline Patient Configuration
                      </Typography>
                    </Grid>

                    <Grid container item xs={12} spacing={2}>
                      <Grid container item xs={12} md={12} spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1">Blood Pressure</Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Stack sx={{ pb: 1 }}>
                            <Typography variant="subtitle2">Systolic</Typography>
                          </Stack>

                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Upper"
                              {...getFieldProps('patient-lifeline-config-bp_systolic_upper')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">mmHg</InputAdornment>,
                              }}
                            />

                            <TextField
                              fullWidth
                              label="Lower"
                              {...getFieldProps('patient-lifeline-config-bp_systolic_lower')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">mmHg</InputAdornment>,
                              }}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Stack sx={{ pb: 1 }}>
                            <Typography variant="subtitle2">Diastolic</Typography>
                          </Stack>

                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Upper"
                              {...getFieldProps('patient-lifeline-config-bp_diastolic_upper')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">mmHg</InputAdornment>,
                              }}
                            />

                            <TextField
                              fullWidth
                              label="Lower"
                              {...getFieldProps('patient-lifeline-config-bp_diastolic_lower')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">mmHg</InputAdornment>,
                              }}
                            />
                          </Stack>
                        </Grid>
                      </Grid>

                      <Grid container item xs={12} md={12} spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" sx={{ pb: 1 }}>
                            Heart Rate
                          </Typography>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Upper"
                              {...getFieldProps('patient-lifeline-config-heartrate_upper_bpm')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">BPM</InputAdornment>,
                              }}
                            />

                            <TextField
                              fullWidth
                              label="Lower"
                              {...getFieldProps('patient-lifeline-config-heartrate_lower_bpm')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">BPM</InputAdornment>,
                              }}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" sx={{ pb: 1 }}>
                            Pulse Rate
                          </Typography>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Upper"
                              {...getFieldProps('patient-lifeline-config-pulserate_upper_bpm')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">BPM</InputAdornment>,
                              }}
                            />

                            <TextField
                              fullWidth
                              label="Lower"
                              {...getFieldProps('patient-lifeline-config-pulserate_lower_bpm')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">BPM</InputAdornment>,
                              }}
                            />
                          </Stack>
                        </Grid>
                      </Grid>

                      <Grid container item xs={12} md={12} spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" sx={{ pb: 1 }}>
                            Oxygen Saturation
                          </Typography>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Upper"
                              {...getFieldProps('patient-lifeline-config-oxygen_upper_saturation')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              }}
                            />

                            <TextField
                              fullWidth
                              label="Lower"
                              {...getFieldProps('patient-lifeline-config-oxygen_lower_saturation')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              }}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" sx={{ pb: 1 }}>
                            Respiratory Rate
                          </Typography>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Upper"
                              {...getFieldProps('patient-lifeline-config-respiratory_upper_rpm')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">RPM</InputAdornment>,
                              }}
                            />

                            <TextField
                              fullWidth
                              label="Lower"
                              {...getFieldProps('patient-lifeline-config-respiratory_lower_rpm')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">RPM</InputAdornment>,
                              }}
                            />
                          </Stack>
                        </Grid>
                      </Grid>

                      <Grid container item xs={12} md={12} spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" sx={{ pb: 1 }}>
                            Temperature
                          </Typography>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Upper"
                              {...getFieldProps('patient-lifeline-config-temperature_upper')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">&deg;C</InputAdornment>,
                              }}
                            />

                            <TextField
                              fullWidth
                              label="Lower"
                              {...getFieldProps('patient-lifeline-config-temperature_lower')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">&deg;C</InputAdornment>,
                              }}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="subtitle1" sx={{ pb: 1 }}>
                            ECG
                          </Typography>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="ST"
                              {...getFieldProps('patient-lifeline-config-ecg_st_msec')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">msec</InputAdornment>,
                              }}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="subtitle1" sx={{ pb: 1 }}>
                            Time Frame
                          </Typography>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Time Frame"
                              {...getFieldProps('patient-lifeline-config-time_frame')}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                              }}
                            />
                          </Stack>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid container sx={{ pt: 3 }}>
                      <Grid item xs={12} md={8}>
                        <Typography variant="caption" sx={{ pt: 1 }}>
                          * Required Fields
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <Button
                            fullWidth
                            size="large"
                            variant="contained"
                            component={RouterLink}
                            to={isEdit ? `/patient/details/${params.id}` : '/patient/list'}
                            color="error"
                          >
                            Cancel
                          </Button>
                          <LoadingButton
                            fullWidth
                            size="large"
                            type="submit"
                            variant="contained"
                            loading={isSubmitting}
                          >
                            {isEdit ? 'Update' : 'Register'}
                          </LoadingButton>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Form>
                </FormikProvider>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
